import { useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  ICompositionComponent,
  ICompositionMpnComponent,
  IGlobalProduct,
  IMpnComposition,
  ITcc,
  ITccComposition,
  TccCompositionRegionInfo,
} from "../../../../types/data.interface";
import AddMpnCompositionModal, {
  CompositionType,
  EditCompositionParams,
} from "../../components/mpn-composition-modal/AddMpnCompositionModal";
import { AddEditSkuCompositionComponentForm } from "../../components/mpn-composition-tree";
import {
  checkIfAtLeastOneComponentIsTheSameInList,
  checkIfSkuIdIsIncludedInNode,
  findNodeIndexByNodeId,
  generateNodeId,
  getNodeByNodeId,
  getUpdatedComponentsList,
} from "./mpn-create.helper";
import { ListingModalRef } from "components/ButtonModal";
import Modal from "components/Modal";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";
import MpnCompositionTree from "../../components/mpn-composition-tree/MpnCompositionTree";
import { COMPOSITION_OPTIONS } from "../../../skus/components/sku-composition-modal/AddSkuCompositionModal";
import ButtonIconModal from "../../../../components/ButtonIconModal";

export type CompositionProps = {
  selectedGP?: IGlobalProduct | null;
  selectedTcc?: ITcc | null;
  savedSkuComposition?: IMpnComposition;
  saveSkuComposition?: (composition: IMpnComposition) => void;
  setUnsavedChangesToTrue: () => void;
  selectedTccCompositionRegion?: TccCompositionRegionInfo | null;
  skuIdInRevision?: string;
  tccComposition?: ITccComposition[];
};

const Composition = ({
  skuIdInRevision,
  setUnsavedChangesToTrue,
  saveSkuComposition,
}: CompositionProps) => {
  const { state, dispatch } = useCreateMpnStore();

  const [showSkuCompositionModal, setShowSkuCompositionModal] = useState(false);
  const modalRef = useRef<ListingModalRef>(null);

  // @ts-ignore
  const [mpnComposition, setMpnComposition] = useState<IMpnComposition>(
    state.composition,
  );
  const [editModalParams, setEditModalParams] =
    useState<EditCompositionParams | null>();

  const [component, setComponent] = useState<ICompositionMpnComponent>(
    {} as ICompositionMpnComponent,
  );

  const startEditProcess = (
    compositionEditType: CompositionType,
    compositionEditObject: ICompositionMpnComponent | IMpnComposition,
  ) => {
    if (compositionEditType === "mpn") {
      setEditModalParams({
        compositionEditType: compositionEditType,
        compositionEditObject: compositionEditObject,
      });

      setShowSkuCompositionModal(true);
    } else {
      //setComponent(compositionEditObject as ICompositionComponent);
const comp = compositionEditObject as ICompositionMpnComponent;

  if (comp.type === "NonMolded") {
    setComponent({
      ...comp,
      sequence_number:
        (comp.full_objects?.non_mold as any)?.sequence_number ??
        comp.sequence_number,
      mold_description:
        comp.full_objects?.non_mold?.name ?? comp.mold_description,
      material_id:
        comp.full_objects?.non_mold?.material?.four_digit_code ??
        comp.material_id,
      material_description:
        comp.full_objects?.non_mold?.material?.name ??
        comp.material_description,
      color_id:
        // @ts-ignore
        comp.full_objects?.non_mold?.color?.id ?? comp.color_id,
      color_description:
        // @ts-ignore
        comp.full_objects?.non_mold?.color?.name ?? comp.color_description,
    });
  } else {
    setComponent(comp);
  }
    }
  };

  const addMpnToComposition = (mpn: IMpnComposition | null, nodeId: number) => {
    let updatedSkuList: IMpnComposition[] = mpnComposition.mpns;

    if (
      mpn &&
      skuIdInRevision &&
      checkIfSkuIdIsIncludedInNode(mpn, skuIdInRevision)
    ) {
      toast.error(
        "You can't include the same MPN you are revising to the composition",
      );
      return;
    }

    if (nodeId == -1 && mpn) {
      updatedSkuList = [
        ...updatedSkuList,
        { ...mpn, node_id: generateNodeId() },
      ];

      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...state.composition,
          mpns: [
            ...state.composition.mpns,
            { ...mpn, node_id: generateNodeId() },
          ],
        }),
      );

      toast.success(
        `Successfully added MPN 11 ${mpn.id.padStart(6, "0")} to composition`,
      );
    } else if (nodeId !== -1) {
      const oldNodeIndex: number = findNodeIndexByNodeId(
        nodeId,
        state.composition.mpns,
      );

      if (mpn) {
          dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...state.composition,
          //@ts-ignore
          mpns: state.composition.mpns?.splice(oldNodeIndex, 1, mpn),
        }),
      );
        // updatedSkuList.splice(oldNodeIndex, 1, mpn);
      } else {
        // updatedSkuList.splice(oldNodeIndex, 1);
        dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
          ...state.composition,
          //@ts-ignore
          mpns: state.composition.mpns?.splice(oldNodeIndex, 1),
        }),
      );
      }
    }

    setMpnComposition({ ...mpnComposition, mpns: updatedSkuList });
    setUnsavedChangesToTrue();
  };

  const duplicateSku = (nodeId: number) => {
    const duplicatedSku: IMpnComposition | undefined = mpnComposition.mpns.at(
      findNodeIndexByNodeId(nodeId, mpnComposition.mpns),
    );
    if (duplicatedSku) addMpnToComposition(duplicatedSku, -1);
  };

  const handleAdditionOfComponents = (
    moldedComponents: ICompositionComponent[],
    nonMoldedComponents: ICompositionComponent[],
    nodeId: number,
    isNewComponent: boolean,
  ) => {
    if (
      checkIfAtLeastOneComponentIsTheSameInList(
        moldedComponents,
        mpnComposition.molded_components,
        isNewComponent,
      )
    ) {
      toast.error(
        `You're trying to add at least one Molded component that already exists. Please change something on the new component or alter the quantity of the existing one`,
      );
      return;
    }

    if (
      checkIfAtLeastOneComponentIsTheSameInList(
        nonMoldedComponents,
        mpnComposition.non_molded_components,
        isNewComponent,
      )
    ) {
      toast.error(
        `You're trying to add at least one Non-Molded component that already exists. Please change something on the new component or alter the quantity of the existing one`,
      );
      return;
    }

    dispatch(
      CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
        ...state.composition,
        molded_components: getUpdatedComponentsList(
          mpnComposition,
          "Molded",
          moldedComponents,
          nodeId,
        ),
        non_molded_components: getUpdatedComponentsList(
          mpnComposition,
          "NonMolded",
          nonMoldedComponents,
          nodeId,
        ),
      }),
    );

    setUnsavedChangesToTrue();

    if (editModalParams) {
      closeModal();
      toast.success("Successfully edited selected component");
    } else {
      toast.success(`Successfully added components to composition`);
    }
  };

  const changeComponentQuantity = (nodeId: number, newQuantity: number) => {
    const componentToChange: ICompositionMpnComponent | undefined =
      getNodeByNodeId(nodeId, mpnComposition) as ICompositionMpnComponent;
    if (componentToChange) {
      const updatedList = getUpdatedComponentsList(
        mpnComposition,
        componentToChange.type,
        [{ ...componentToChange, quantity: newQuantity }],
        nodeId,
      );
      if (componentToChange.type === "Molded") {
        setMpnComposition({
          ...mpnComposition,
          molded_components: updatedList,
        });
      } else {
        setMpnComposition({
          ...mpnComposition,
          non_molded_components: updatedList,
        });
      }
      setUnsavedChangesToTrue();
    }
  };

  const deleteCompositionNode = (
    component: ICompositionMpnComponent | number,
    compositionType: CompositionType,
  ) => {

    if (component.hasOwnProperty("type")) {
      const moldedNonMoldedKey: keyof IMpnComposition =
        // @ts-ignore
        component.type === "Molded"
          ? "molded_components"
          : "non_molded_components";

      if (component.hasOwnProperty("node_id")) {
        const filteredComponents: ICompositionMpnComponent[] =
          state.composition[moldedNonMoldedKey].filter(
            //@ts-ignore
            (node: ICompositionMpnComponent) => node.node_id != component.node_id,
          );

        dispatch(
          CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
            ...state.composition,
            [moldedNonMoldedKey]: filteredComponents,
          }),
        );
      } else {
        const filteredComponents: ICompositionMpnComponent[] =
          state.composition[moldedNonMoldedKey].filter(
            //@ts-ignore
            (node: ICompositionMpnComponent) => node["mold_id"] !== component["mold_id"],
          );

        dispatch(
          CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
            ...state.composition,
            [moldedNonMoldedKey]: filteredComponents,
          }),
        );
      }

      toast.success(
        `Successfully removed ${
          compositionType === COMPOSITION_OPTIONS.SKU ? "MPN" : `Component`
        } from composition`,
      );
      return;

      // const updatedComposition = state.composition[moldedNonMoldedKey];
      // const filteredSkus: IMpnComposition[] = state.composition.skus.filter(
      //   (node: IMpnComposition) => node.node_id != node_id,
      // );
    }


   if (typeof component === 'number' && component > 0) {
     const filteredComponents: ICompositionMpnComponent[] =
       state.composition.mpns?.filter(
         //@ts-ignore
         (node: ICompositionMpnComponent) => node.node_id !== component,
       );

     dispatch(
       CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
         ...state.composition,
         mpns: filteredComponents,
       }),
     );
   }else{
     console.log(component)
     const filteredComponents: ICompositionMpnComponent[] =
       state.composition.mpns?.filter(
         //@ts-ignore
         (node: ICompositionMpnComponent) => node["id"] !== component,
       );

     console.log(filteredComponents, component, compositionType);

     dispatch(
       CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_COMPOSITION, {
         ...state.composition,
         mpns: filteredComponents,
       }),
     );
   }

    toast.success(
      `Successfully removed ${
        compositionType === COMPOSITION_OPTIONS.SKU ? "MPN" : `Component`
      } from composition`,
    );
    return;
  };

  const closeModal = () => {
    setShowSkuCompositionModal(false);
    setEditModalParams(null);
  };

  return (
    <>
      <Modal
        showModal={showSkuCompositionModal}
        onClose={closeModal}
        title={`${
          editModalParams
            ? `Edit ${
                editModalParams.compositionEditType === "mpn"
                  ? `MPN 11 ${editModalParams.compositionEditObject.id} - ${editModalParams.compositionEditObject.name}`
                  : `Component ${editModalParams.compositionEditObject.mold_description}`
              }`
            : ""
        }`}
        customStyle={"mold-comps"}
      >
        <AddMpnCompositionModal
          parentModalRef={modalRef}
          handleAddSku={addMpnToComposition}
          handleAddComponents={handleAdditionOfComponents}
          editModalParams={editModalParams}
          handleCloseModal={closeModal}
          confirmLabel="Save"
          informativeMessage="If the MPN is selected all of its composition is added. If it is
                not selected, the user can select the components to add"
        />
      </Modal>

      {/*<HierarchyInfoSection tcc={} gp={} />*/}

      <div className="card-header my-2 rounded d-flex justify-content-between">
        <h6 className="m-0 text-primary fw-bold">MPN Composition</h6>
      </div>

      <div className="d-flex justify-content-between mb-2">
        <p className="font-italic">
          {`${
            state?.composition.mpns?.length === 0 &&
            state?.composition.molded_components?.length === 0 &&
            state?.composition.non_molded_components?.length === 0
              ? "Your MPN has no components associated to it."
              : ""
          } Please manage the composition below click on "Add MPN" to add a MPN'`}
        </p>

        <ButtonIconModal
          buttonIconClass="d-inline-block"
          description="Add MPN"
          title={""}
          buttonStyleClass="btn-primary"
          ref={modalRef}
        >
          <AddMpnCompositionModal
            parentModalRef={modalRef}
            handleAddSku={addMpnToComposition}
            handleAddComponents={handleAdditionOfComponents}
            editModalParams={editModalParams}
            confirmLabel="Add to MPN"
            informativeMessage="If the MPN is selected all of its composition is added. If it is
                not selected, the user can select the components to add"
          />
        </ButtonIconModal>
      </div>

      <AddEditSkuCompositionComponentForm
        component={component}
        setComponent={setComponent}
      />

      <MpnCompositionTree
        composition={state.composition}
        isMainPage={true}
        duplicateActionHandler={duplicateSku}
        changeQuantityActionHandler={changeComponentQuantity}
        handleRemoveNode={deleteCompositionNode}
        openEditModal={startEditProcess}
        shouldIncludeSku={false}
        manageSkuInclusion={undefined}
        manageComponentInclusion={undefined}
        assertComponentIsIncluded={undefined}
      />
    </>
  );
};

export default Composition;
